import { getSuspendedSellers, getSuspensionStats, reviewSellerAppeal, ReviewAppealData } from "@/api/User/users.api";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "react-toastify";

export function useSuspendedSellers(appealStatus?: string) {
    const { data, isLoading, isFetching, fetchNextPage, isSuccess, isFetchingNextPage, error, refetch } = useInfiniteQuery({
        queryKey: ['suspendedSellers', appealStatus],
        queryFn: ({ pageParam }) => getSuspendedSellers({ cursor: pageParam, appealStatus }),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.nextCursor
    });

    const suspensions = useMemo(
        () => data?.pages.flatMap((page) => page.suspensions) ?? [],
        [data]
    );

    return {
        data: suspensions,
        isLoading,
        isSuccess,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        error,
        refetch,
    };
}

export function useSuspensionStats() {
    return useQuery({
        queryKey: ['suspensionStats'],
        queryFn: getSuspensionStats,
        staleTime: 30000, // 30 seconds
    });
}

export function useReviewAppeal() {
    const queryClient = useQueryClient();

    const { data, isSuccess, isPending, mutate, mutateAsync } = useMutation({
        mutationFn: ({ suspensionId, data }: { suspensionId: string; data: ReviewAppealData }) => {
            return reviewSellerAppeal(suspensionId, data);
        },

        onError: (err) => {
            console.log(err);
            toast.error("Failed to review appeal");
        },
        onSuccess: async (data, variables) => {
            const message = variables.data.approved
                ? "Appeal approved - seller access restored"
                : "Appeal rejected";
            toast.success(message);

            // Invalidate queries to refresh data
            await queryClient.invalidateQueries({ queryKey: ['suspendedSellers'] });
            await queryClient.invalidateQueries({ queryKey: ['suspensionStats'] });
            await queryClient.invalidateQueries({ queryKey: ['usersAdmin'] });
        }
    });

    return {
        data,
        isPending,
        isSuccess,
        mutateAsync,
        mutate
    };
}
